import { ReactElement, useState, useCallback, useEffect, useMemo } from 'react';

import { useHistory, useParams } from 'react-router-dom';

import {
  Table,
  GoBackButton,
  Pagination,
  InputSearch,
  DefaultSelect,
} from '~/components';
import { ISearch } from '~/components/Pagination/Search';
import { useLoading } from '~/hooks/loading';
import { IPackage, IPagination, PageOptionsRequest } from '~/models/Common';
import { ProfessionalService } from '~/services';

import * as S from './styles';

interface IPackagesParams {
  id: string;
}

interface ITypeOptions {
  label: string;
  value: string;
}

const selectOptions: ITypeOptions[] = [
  { label: 'Ativo', value: 'active' },
  { label: 'Inativo', value: 'inactive' },
];

function Packages(): ReactElement {
  const { setLoading } = useLoading();

  const [searchFilter, setSearchFilter] = useState('');
  const [status, setStatus] = useState('');
  const [packages, setPackages] = useState<IPagination<IPackage[]>>({
    data: [],
    lastPage: 0,
    page: 1,
    perPage: 1,
    total: '0',
  });
  const [pageOptions, setPageOptions] = useState<PageOptionsRequest>({
    page: 1,
  });
  const { id } = useParams<IPackagesParams>();
  const router = useHistory();

  const handleGetUserPackages = async (data: ISearch) => {
    setLoading(true);

    const response = await ProfessionalService.getUserPackages(id, data);

    if (searchFilter) {
      setPackages({
        ...response,
        data: response.data?.data?.filter(
          (i) =>
            i.status === searchFilter ||
            String(i.number_of_training_programs) === searchFilter ||
            i.name === searchFilter,
        ),
      });
    } else {
      setPackages(response.data);
    }

    setLoading(false);
  };

  const handleNavigateToPackageDetail = useCallback(
    (packageId: string, packageData: IPackage) => {
      router.push(`/users/details/${id}/packages/${packageId}`, {
        packageData,
      });
    },
    [id, router],
  );

  useEffect(() => {
    handleGetUserPackages({
      searchFilter,
      status,
      page: pageOptions.page,
      perpage: 9,
    });
  }, [status, searchFilter, pageOptions]);

  const PackagesTableMemo = useMemo(
    () => (
      <Table colsNames={['Nome', 'N° de programas', 'Status', '']}>
        {packages.data?.map((packageData) => (
          <tr
            key={packageData.id}
            onClick={() =>
              handleNavigateToPackageDetail(packageData.id, packageData)
            }
          >
            <td>{packageData.name}</td>
            <td>{packageData.number_of_training_programs}</td>
            <td>
              {packageData.status === 'active' ? (
                <span style={{ color: 'green' }}>Ativo</span>
              ) : (
                <span style={{ color: 'red' }}>Inativo</span>
              )}
            </td>
            <td>{'>'}</td>
          </tr>
        ))}
      </Table>
    ),
    [handleNavigateToPackageDetail, packages.data],
  );

  return (
    <>
      <GoBackButton
        containerStyle={{ alignSelf: 'flex-start', paddingTop: '2.2rem' }}
      />
      <S.Container>
        <S.PackagesHeader>
          <S.PackagesTitle>Pacotes de Carlos Paz</S.PackagesTitle>
          <DefaultSelect
            name="validity"
            initialValue={{ label: 'Todos os status', value: '' }}
            options={selectOptions}
            onChange={(v) => setStatus(v.target.value)}
          />
          <InputSearch
            name="package-search"
            placeholder="Buscar por nº de programas"
            onSearch={(value) => setSearchFilter(value)}
          />
        </S.PackagesHeader>
        {PackagesTableMemo}
        <S.PackagesFooter>
          <Pagination
            pageCount={packages.lastPage!}
            pageRangeDisplayed={3}
            {...{ setPageOptions }}
          />
        </S.PackagesFooter>
      </S.Container>
    </>
  );
}

export default Packages;
